// src/client/utils/idleTimer.js
// ------------------------------------------------------------
// FINAL STABLE IDLE TIMER (CLEAN + SAFE + PREDICTABLE)
// ------------------------------------------------------------
import { sysConfig } from "~shared/config/system.cfg.js";
import ActivityManager from "@/services/ActivityManager";

// ------------------------------------------------------------
// INTERNAL STATE
// ------------------------------------------------------------
let idleTimeoutId = null;
let heartbeatId = null;
let attached = false;

let idleDelayMs = sysConfig.idleDelayMs;
let heartbeatMs = 1000;

let idleStartTs = Date.now();
let onIdle = null;
let onProgress = null;

// ------------------------------------------------------------
// User Activity Events (real input only)
// ------------------------------------------------------------
const ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "click",
  "scroll",
  "touchstart"
];

// ---- Mouse jitter filter ----
let lastX = null;
let lastY = null;
let lastTs = 0;

function isRealMouseMove(evt) {
  if (evt.type !== "mousemove") return true;

  if (evt.clientX === lastX && evt.clientY === lastY) return false;

  const now = performance.now();
  if (now - lastTs < 25) return false;

  lastTs = now;
  lastX = evt.clientX;
  lastY = evt.clientY;

  return true;
}

// ------------------------------------------------------------
// Idle guard (NEW, single responsibility)
// ------------------------------------------------------------
function maybeTriggerIdle() {
  if (ActivityManager.isBusy.value) {
    console.log("⏸️ Idle reached but activity in progress — deferring logout");

    // Optional grace period before re-check
    startIdleCountdown(30_000);
    return;
  }

  console.log("🔥 IDLE TIMEOUT REACHED");
  onIdle?.();
}

// ------------------------------------------------------------
// IDLE COUNTDOWN
// ------------------------------------------------------------
function startIdleCountdown(customMs) {
  clearTimeout(idleTimeoutId);

  idleStartTs = Date.now();
  const delay = customMs ?? idleDelayMs;

  idleTimeoutId = setTimeout(() => {
    maybeTriggerIdle();
  }, delay);
}

// ------------------------------------------------------------
// HEARTBEAT (visual progress updates)
// ------------------------------------------------------------
function startHeartbeat() {
  clearInterval(heartbeatId);

  heartbeatId = setInterval(() => {
    const now = Date.now();
    const elapsed = now - idleStartTs;

    const pct = Math.min(1, elapsed / idleDelayMs);
    onProgress?.(pct);

    if (elapsed >= idleDelayMs) {
      clearInterval(heartbeatId);
      heartbeatId = null;
      maybeTriggerIdle();
    }
  }, heartbeatMs);
}

// ------------------------------------------------------------
// Attach listeners ONCE only
// ------------------------------------------------------------
function attachListeners() {
  if (attached) return;
  attached = true;

  ACTIVITY_EVENTS.forEach(event => {
    window.addEventListener(
      event,
      evt => {
        if (evt.target.closest?.(".logout-icon")) return;
        if (event === "mousemove" && !isRealMouseMove(evt)) return;

        resetIdleTimer();
      },
      { passive: true }
    );
  });
}

// ------------------------------------------------------------
// PUBLIC API
// ------------------------------------------------------------
export function initIdleTimer(idleCb, options = {}) {
  stopIdleTimer();

  onIdle = idleCb;

  idleDelayMs = options.timeoutMs ?? sysConfig.idleDelayMs;
  heartbeatMs = options.heartbeatMs ?? 1000;
  onProgress = options.onProgress ?? null;

  idleStartTs = Date.now();

  attachListeners();
  startIdleCountdown();
  startHeartbeat();
}

export function resetIdleTimer(customMs) {
  startIdleCountdown(customMs);
}

export function stopIdleTimer() {
  clearTimeout(idleTimeoutId);
  idleTimeoutId = null;

  clearInterval(heartbeatId);
  heartbeatId = null;
}
